/** Tiny DOM helpers — no framework, just fewer keystrokes. */

type Child = Node | string | number | null | undefined | false;
type Props = {
  class?: string;
  text?: string;
  html?: string;
  style?: Partial<CSSStyleDeclaration> | string;
  attrs?: Record<string, string | number | boolean | undefined>;
  dataset?: Record<string, string>;
  on?: { [K in keyof HTMLElementEventMap]?: (e: HTMLElementEventMap[K]) => void };
};

export function h<K extends keyof HTMLElementTagNameMap>(tag: K, props: Props | string = {}, children: Child[] = []): HTMLElementTagNameMap[K] {
  const el = document.createElement(tag);
  const p: Props = typeof props === 'string' ? { class: props } : props;
  if (p.class) el.className = p.class;
  if (p.text != null) el.textContent = p.text;
  if (p.html != null) el.innerHTML = p.html;
  if (typeof p.style === 'string') el.style.cssText = p.style;
  else if (p.style) Object.assign(el.style, p.style);
  if (p.attrs) for (const [k, v] of Object.entries(p.attrs)) {
    if (v === false || v === undefined) continue;
    el.setAttribute(k, v === true ? '' : String(v));
  }
  if (p.dataset) Object.assign(el.dataset, p.dataset);
  if (p.on) for (const [k, fn] of Object.entries(p.on)) el.addEventListener(k, fn as EventListener);
  for (const c of children) if (c !== null && c !== undefined && c !== false) el.append(typeof c === 'number' ? String(c) : c);
  return el;
}

const SVGNS = 'http://www.w3.org/2000/svg';
export function svg<K extends keyof SVGElementTagNameMap>(tag: K, attrs: Record<string, string | number> = {}, children: SVGElement[] = []): SVGElementTagNameMap[K] {
  const el = document.createElementNS(SVGNS, tag);
  for (const [k, v] of Object.entries(attrs)) el.setAttribute(k, String(v));
  for (const c of children) el.append(c);
  return el;
}

/** Set textContent only when it changed (avoids layout/style churn per frame). */
export function setText(el: Element, text: string): void {
  if (el.textContent !== text) el.textContent = text;
}

export function toggleClass(el: Element, cls: string, on: boolean): void {
  if (el.classList.contains(cls) !== on) el.classList.toggle(cls, on);
}

/** A <button> styled as a hardware key. */
export function keyButton(opts: { label: string; key?: string; cls?: string; title?: string; onClick?: (e: MouseEvent) => void }): HTMLButtonElement {
  return h('button', {
    class: `sd-key ${opts.cls ?? ''}`,
    attrs: { type: 'button', title: opts.title, 'data-key': opts.key },
    on: opts.onClick ? { click: opts.onClick } : {},
  }, [
    opts.key ? h('span', { class: 'sd-key__cap', text: opts.key }) : null,
    h('span', { class: 'sd-key__label', text: opts.label }),
    h('span', { class: 'sd-key__led' }),
  ]);
}

export function prefersReducedMotion(): boolean {
  return document.documentElement.classList.contains('sd-reduced-motion')
    || (typeof matchMedia !== 'undefined' && matchMedia('(prefers-reduced-motion: reduce)').matches);
}
